/**
 * Site Analysis Dashboard — Dual-pane view showing both agents side by side.
 *
 * Displays:
 * - Frontend Explorer agent progress (left panel)
 * - API Explorer agent progress (right panel)
 * - Phase stepper, progress bar, current action, entity log per agent
 * - Embedded live browser view when available
 */

import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Link,
} from '@mui/material';
import {
  Language as FrontendIcon,
  Api as ApiIcon,
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
  HourglassEmpty as WaitingIcon,
} from '@mui/icons-material';
import { SiteAnalysisProgress, AgentProgressState } from '../types';
import TMSEntityCreationProgress from './TMSEntityCreationProgress';
import LiveExecutionPanel from './LiveExecutionPanel';

const PHASES = [
  'Exploring',
  'Creating Entities',
  'Executing Tests',
  'Generating Code',
  'Done',
];

const STATUS_COLORS: Record<string, 'info' | 'success' | 'error' | 'warning' | 'default'> = {
  initializing: 'default',
  exploring: 'info',
  creating_entities: 'info',
  executing: 'warning',
  generating_code: 'info',
  completed: 'success',
  failed: 'error',
};

function getActiveStep(status: string): number {
  switch (status) {
    case 'initializing':
      return 0;
    case 'exploring':
      return 0;
    case 'creating_entities':
      return 1;
    case 'executing':
      return 2;
    case 'generating_code':
      return 3;
    case 'completed':
      return 4;
    case 'failed':
      return -1;
    default:
      return 0;
  }
}

interface AgentPanelProps {
  title: string;
  icon: React.ReactNode;
  agent: AgentProgressState;
  qaInfraUrl?: string;
}

const AgentPanel: React.FC<AgentPanelProps> = ({ title, icon, agent, qaInfraUrl }) => {
  const activeStep = getActiveStep(agent.status);
  const chipColor = STATUS_COLORS[agent.status] || 'default';

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={agent.status.replace(/_/g, ' ').toUpperCase()}
          color={chipColor}
          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
        />
      </Box>

      {/* Phase Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
        {PHASES.map((label, idx) => (
          <Step
            key={label}
            completed={activeStep > idx}
          >
            <StepLabel
              error={agent.status === 'failed' && activeStep === idx}
              sx={{
                '& .MuiStepLabel-label': { fontSize: '0.7rem' },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Progress
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {agent.progress_pct}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={agent.progress_pct}
          color={agent.status === 'failed' ? 'error' : 'primary'}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {/* Current Action */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2,
          minHeight: 20,
          fontStyle: agent.status === 'completed' ? 'normal' : 'italic',
        }}
      >
        {agent.current_action || 'Waiting...'}
      </Typography>

      {/* Error */}
      {agent.error && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="caption" color="error.dark">
            {agent.error}
          </Typography>
        </Box>
      )}

      {/* Live Browser View */}
      {agent.live_stream_url && qaInfraUrl && (
        <Box sx={{ mb: 2 }}>
          <LiveExecutionPanel
            sessionId=""
            qaInfraUrl={qaInfraUrl}
            liveStreamUrl={agent.live_stream_url}
            compact
          />
        </Box>
      )}

      {/* Entity Creation Log */}
      {agent.entities_created && agent.entities_created.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <TMSEntityCreationProgress entities={agent.entities_created} />
        </>
      )}
    </Paper>
  );
};

interface SiteAnalysisDashboardProps {
  progress: SiteAnalysisProgress;
  qaInfraUrl?: string;
}

const SiteAnalysisDashboard: React.FC<SiteAnalysisDashboardProps> = ({
  progress,
  qaInfraUrl,
}) => {
  const feAgent = progress.agents?.frontend_explorer;
  const apiAgent = progress.agents?.api_explorer;

  if (!feAgent && !apiAgent) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Site Analysis
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {progress.url}
        </Typography>
      </Box>

      {/* Dual Agent Panels */}
      <Grid container spacing={2}>
        {feAgent && (
          <Grid item xs={12} md={6}>
            <AgentPanel
              title="Frontend Explorer"
              icon={<FrontendIcon color="primary" fontSize="small" />}
              agent={feAgent}
              qaInfraUrl={qaInfraUrl}
            />
          </Grid>
        )}
        {apiAgent && (
          <Grid item xs={12} md={6}>
            <AgentPanel
              title="API Explorer"
              icon={<ApiIcon color="secondary" fontSize="small" />}
              agent={apiAgent}
              qaInfraUrl={qaInfraUrl}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default SiteAnalysisDashboard;
